import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  loadDoDqiAccountsIntoDuckDb,
  loadDoDqiDetail,
  loadDoDqiSummaryIntoDuckDb,
  queryDoDqiAccounts,
  resetDoDqiData,
} from './data/browserDuckDb';
import DtaiDqiDashboard from './dtaiDqi/DtaiDqiDashboard';
import './App.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://genaiapi.enterprise360uat.genpact.com/wd-tfa/doDqi';

const DO_DQI_API_ENDPOINTS = {
  performance: `${API_BASE_URL}/dashboard/do-dqi/performance`,
  capability: `${API_BASE_URL}/dashboard/do-dqi/capability`,
  clientFeedback: `${API_BASE_URL}/dashboard/do-dqi/client-feedback`,
  responsiveness: `${API_BASE_URL}/dashboard/do-dqi/responsiveness`,
  overall: `${API_BASE_URL}/dashboard/do-dqi/overall`,
  summary: `${API_BASE_URL}/dashboard/do-dqi/summary`,
  summaryVariation: `${API_BASE_URL}/dashboard/do-dqi/summary-variation`,
  accounts: `${API_BASE_URL}/dashboard/do-dqi/accounts`,
  filters: `${API_BASE_URL}/dashboard/do-dqi/filters`,
  sqlQuery: `${API_BASE_URL}/sqlquery`,
};

const DTAI_DQI_API_ENDPOINTS = {
  summary: `${API_BASE_URL}/dashboard/dtai-dqi/summary`,
};

const ACCOUNTS_PAGE_SIZE = 10;
const FILTER_FIRST_PAGE_SIZE = 50;
const FILTER_NEXT_PAGE_SIZE = 10;
const SHARED_DQI_FILTERS_KEY = 'mosaic.sharedDqiFilters';
const SHARED_DQI_FILTERS_VERSION = 2;

const defaultFilters = {
  unitId: '',
  parentAccountId: '',
  custAcctId: '',
  custAcctLabels: '',
  srvcLnId: '',
  rgnId: '',
  revenue: '',
  year: '2025',
  period: '20250430',
  headcount: '',
};

const readSharedFilters = (fallback) => {
  if (typeof window === 'undefined') return fallback;
  try {
    const stored = window.localStorage.getItem(SHARED_DQI_FILTERS_KEY);
    if (!stored) return fallback;
    const parsed = JSON.parse(stored);
    const migratedFilters = { ...fallback, ...parsed };
    if (parsed._version !== SHARED_DQI_FILTERS_VERSION && parsed.headcount === '>=100') {
      migratedFilters.headcount = '';
    }
    if (migratedFilters.srvcLnId === 'Finance - Accounting') {
      migratedFilters.srvcLnId = '94';
    }
    delete migratedFilters._version;
    return migratedFilters;
  } catch {
    return fallback;
  }
};

const writeSharedFilters = (filters) => {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(SHARED_DQI_FILTERS_KEY, JSON.stringify({
    ...filters,
    _version: SHARED_DQI_FILTERS_VERSION,
  }));
};

const periodOptions = [
  { label: 'All', value: '' },
  { label: 'JANUARY', value: '20250131' },
  { label: 'FEBRUARY', value: '20250228' },
  { label: 'MARCH', value: '20250331' },
  { label: 'APRIL', value: '20250430' },
  { label: 'MAY', value: '20250531' },
  { label: 'JUNE', value: '20250630' },
  { label: 'JULY', value: '20250731' },
  { label: 'AUGUST', value: '20250831' },
  { label: 'SEPTEMBER', value: '20250930' },
  { label: 'OCTOBER', value: '20251031' },
  { label: 'NOVEMBER', value: '20251130' },
  { label: 'DECEMBER', value: '20251231' },
];

const serviceLineOptions = [
  { label: 'All', value: '' },
  { label: 'Capital Markets', value: 'Capital Markets' },
  { label: 'Claims', value: 'Claims' },
  { label: 'Commercial Services', value: 'Commercial Services' },
  { label: 'Customer Care', value: '121' },
  { label: 'F&A Multi-tower', value: 'F&A Multi-tower' },
  { label: 'Finance - Accounts Payable', value: '85' },
  { label: 'Finance - Invoice To Cash', value: '94' },
];

const deliveryRegionOptions = [
  { label: 'All', value: '' },
  { label: 'NAM', value: '1' },
  { label: 'AUSTRALIA', value: '2' },
  { label: 'CHINA', value: '3' },
  { label: 'EMEA', value: '4' },
  { label: 'INDIA', value: '5' },
  { label: 'PHILIPPINES', value: '6' },
  { label: 'LATAM', value: '7' },
];

const headcountOptions = [
  { label: 'All', value: '' },
  { label: '>=100', value: '>=100' },
  { label: '>=50', value: '>=50' },
];

const revenueOptions = [
  { label: 'All', value: '' },
  { label: '(Blank)', value: '(Blank)' },
  { label: '<5M', value: '<5M' },
  { label: '>10M', value: '>10M' },
  { label: '5M-10M', value: '5M-10M' },
];

const reportingYearOptions = [
  { label: '2024', value: '2024' },
  { label: '2025', value: '2025' },
  { label: '2026', value: '2026' },
];

const DO_DQI_DETAIL_TABLE_COLUMNS = [
  { apiField: 'subParameter', label: 'Sub Parameter', type: 'text' },
  { apiField: 'complianceScore', label: 'Compliance Score', type: 'percent' },
  { apiField: 'metricsMissed', label: 'Metrics Missed', digits: 0 },
  { apiField: 'fatalParameters', label: 'Fatal Parameters', digits: 0 },
  { apiField: 'actualScore', label: 'Actual Score (A)', type: 'percent' },
  { apiField: 'weightage', label: 'Weightage (B)', type: 'text' },
  { apiField: 'weightedScore', label: 'Weighted Score (A*B)', type: 'percent' },
];

const DO_DQI_DETAIL_PLACEHOLDERS = [
  { key: 'performance', title: 'A) Performance', data: [], isLoading: true },
  { key: 'capability', title: 'B) Capability', data: [], isLoading: true },
  { key: 'clientFeedback', title: 'C) Client Feedback', data: [], isLoading: true },
  { key: 'responsiveness', title: 'D) Responsiveness', data: [], isLoading: true },
];

const formatNumber = (value, digits = 2) => {
  if (value === null || value === undefined || value === '') return '--';

  const number = Number(value);
  if (!Number.isFinite(number)) return '--';

  return number.toLocaleString(undefined, {
    maximumFractionDigits: digits,
  });
};

const formatText = (value) => (value == null || value === '' ? '--' : value);

const formatPercent = (value, digits = 1) => {
  const formatted = formatNumber(value, digits);
  return formatted === '--' ? formatted : `${formatted}%`;
};

const formatDetailCell = (row, column) => {
  if (
    row.subParameter === 'Transactional Quality'
    && ['metricsMissed', 'fatalParameters'].includes(column.apiField)
    && (row[column.apiField] === null || row[column.apiField] === undefined)
  ) {
    return '';
  }

  if (column.type === 'text') {
    return formatText(row[column.apiField]);
  }

  if (column.type === 'percent') {
    const digits = ['SLAs', 'Transactional Quality'].includes(row.subParameter) && column.apiField === 'complianceScore'
      ? 0
      : column.digits;
    return formatPercent(row[column.apiField], digits);
  }

  return formatNumber(row[column.apiField], column.digits);
};

const normalizeOptionKey = (value) => String(value || '').trim().toLowerCase();

const uniqueOptions = (options, keySelector = (option) => option.value) => {
  const unique = [];
  const seen = new Set();

  options.forEach((option) => {
    const key = normalizeOptionKey(keySelector(option));
    if (!key || seen.has(key)) return;

    seen.add(key);
    unique.push(option);
  });

  return unique;
};

const getPeriodForReportingYear = (period, year) => {
  const selectedPeriod = String(period || '').split(',')[0];
  const selectedYear = String(year || '').split(',')[0];
  if (!selectedPeriod || !selectedYear || !/^\d{8}$/.test(selectedPeriod) || !/^\d{4}$/.test(selectedYear)) return selectedPeriod || period;

  const month = selectedPeriod.slice(4, 6);
  const lastDay = new Date(Number(selectedYear), Number(month), 0).getDate();
  return `${selectedYear}${month}${String(lastDay).padStart(2, '0')}`;
};

const getRevenueFilterValues = (value) => String(value || '')
  .split(',')
  .map((item) => item.trim())
  .filter((item) => item && item.toLowerCase() !== 'all');

const isBlankRevenueValue = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  return normalized === '(blank)' || normalized === 'blank' || normalized === '__blank__';
};

const shouldShowNoDataForRevenue = (filters) => {
  const revenueValues = getRevenueFilterValues(filters.revenue);
  return revenueValues.length > 0 && !revenueValues.some(isBlankRevenueValue);
};

const emptySummary = {
  score: null,
  scoreLabel: '--',
  score_label: '--',
  variation: null,
  variationLabel: null,
  variation_label: null,
  direction: 'up',
};

const blankDetailScores = (detail) => ({
  ...detail,
  data: (detail?.data || detail?.rows || []).map((row) => ({
    ...row,
    complianceScore: null,
    complianceScoreLabel: '--',
    metricsMissed: null,
    metricsMissedLabel: '--',
    fatalParameters: null,
    fatalParametersLabel: '--',
    actualScore: null,
    actualScoreLabel: '--',
    weightedScore: null,
    weightedScoreLabel: '--',
  })),
  rows: undefined,
  overall: detail?.overall
    ? { ...detail.overall, weightedScore: null, weightedScoreLabel: '--' }
    : null,
});

const addDoDqiFilterParams = (params, filters) => {
  if (filters.unitId) params.set('unitId', filters.unitId);
  if (filters.parentAccountId) params.set('parentAccountId', filters.parentAccountId);
  if (filters.custAcctId) params.set('custAcctId', filters.custAcctId);
  if (filters.period) params.set('period', getPeriodForReportingYear(filters.period, filters.year));
  if (filters.rgnId) params.set('rgnId', filters.rgnId);
  if (filters.srvcLnId) params.set('srvcLnId', filters.srvcLnId);
  if (filters.revenue) params.set('revenue', filters.revenue);
  if (filters.year) params.set('year', String(filters.year).split(',')[0]);
  if (filters.headcount) params.set('headcount', filters.headcount);
};

const buildDoDqiDetailUrl = (endpoint, filters) => {
  const params = new URLSearchParams();
  addDoDqiFilterParams(params, filters);

  const query = params.toString();
  return query ? `${endpoint}?${query}` : endpoint;
};

const buildPerformanceUrl = (filters) => buildDoDqiDetailUrl(DO_DQI_API_ENDPOINTS.performance, filters);

const buildSummaryUrl = (filters) => {
  const params = new URLSearchParams();
  addDoDqiFilterParams(params, filters);
  params.set('includePrevious', 'false');
  const query = params.toString();
  return query ? `${DO_DQI_API_ENDPOINTS.summary}?${query}` : DO_DQI_API_ENDPOINTS.summary;
};

const buildSummaryVariationUrl = (filters) => {
  const params = new URLSearchParams();
  addDoDqiFilterParams(params, filters);
  const query = params.toString();
  return query ? `${DO_DQI_API_ENDPOINTS.summaryVariation}?${query}` : DO_DQI_API_ENDPOINTS.summaryVariation;
};

const buildDtaiSummaryUrl = (filters) => {
  const params = new URLSearchParams();
  if (filters.unitId) params.set('unitId', filters.unitId);
  if (filters.custAcctId) params.set('custAcctId', filters.custAcctId);
  if (filters.revenue) params.set('revenue', filters.revenue);
  if (filters.period) params.set('period', getPeriodForReportingYear(filters.period, filters.year));
  if (filters.year) params.set('year', String(filters.year).split(',')[0]);
  const query = params.toString();
  return query ? `${DTAI_DQI_API_ENDPOINTS.summary}?${query}` : DTAI_DQI_API_ENDPOINTS.summary;
};

const buildAccountsUrl = (filters, offset = 0) => {
  const params = new URLSearchParams();
  addDoDqiFilterParams(params, filters);
  params.set('limit', String(ACCOUNTS_PAGE_SIZE));
  params.set('offset', String(Math.max(0, offset)));
  return `${DO_DQI_API_ENDPOINTS.accounts}?${params.toString()}`;
};

const normalizeUnitOptions = (payload) => {
  const units = payload?.filters?.units || [];
  if (!Array.isArray(units)) return [{ label: 'All', value: '' }];

  const unitOptions = units
    .map((unit) => {
      const value = unit.unitId;
      const label = unit.unitName;

      return value == null || value === ''
        ? null
        : { label: String(label), value: String(value) };
    })
    .filter(Boolean);

  return [{ label: 'All', value: '' }, ...unitOptions];
};

const normalizeParentAccountOptions = (payload, selectedUnitId = '') => {
  const parentAccounts = payload?.filters?.parentAccounts || [];
  if (!Array.isArray(parentAccounts)) return [{ label: 'All', value: '' }];

  const parentAccountOptions = parentAccounts
    .filter((parentAccount) => !selectedUnitId || String(parentAccount.unitId) === String(selectedUnitId))
    .map((parentAccount) => {
      const value = parentAccount.parentAccountId;
      const label = parentAccount.parentAccountName;

      return value == null || value === ''
        ? null
        : { label: String(label), value: String(value) };
    })
    .filter(Boolean);

  return [{ label: 'All', value: '' }, ...uniqueOptions(parentAccountOptions, (option) => option.label)];
};

const normalizeAccountOptions = (payload, selectedUnitId = '') => {
  const accounts = payload?.filters?.accounts || payload?.filters?.customerAccounts || [];
  if (!Array.isArray(accounts)) return [{ label: 'All', value: '' }];

  const accountOptions = accounts
    .filter((account) => !selectedUnitId || String(account.unitId) === String(selectedUnitId))
    .map((account) => {
      const value = account.accountId ?? account.customerId ?? account.custAcctId ?? account.accountUid ?? account.value;
      const label = account.accountName ?? account.customerName ?? account.custAcctName ?? account.name ?? account.label ?? value;

      return value == null || value === ''
        ? null
        : { label: String(label), value: String(value) };
    })
    .filter(Boolean);

  return [{ label: 'All', value: '' }, ...uniqueOptions(accountOptions, (option) => option.label)];
};

const normalizeServiceLineOptions = (payload) => {
  const serviceLines = payload?.filters?.serviceLines || [];
  const options = serviceLines
    .map((serviceLine) => {
      const value = serviceLine.serviceLineId ?? serviceLine.serviceLineUid ?? serviceLine.srvcLnId ?? serviceLine.srvc_ln_id;
      const label = serviceLine.serviceLineName ?? serviceLine.serviceLine ?? serviceLine.service_line ?? value;
      return value && label ? { label: String(label), value: String(value) } : null;
    })
    .filter(Boolean);

  return options.length ? [{ label: 'All', value: '' }, ...uniqueOptions(options, (option) => option.label)] : serviceLineOptions;
};

const buildDoDqiFiltersUrl = ({
  unitId,
  parentAccountId,
  custAcctId,
  period,
  rgnId,
  srvcLnId,
  year,
  headcount,
  parentAccountLimit,
  parentAccountOffset,
  accountLimit,
  accountOffset,
  unitSearch,
  parentAccountSearch,
  accountSearch,
} = {}) => {
  const params = new URLSearchParams();
  if (unitId) params.set('unitId', unitId);
  if (parentAccountId) params.set('parentAccountId', parentAccountId);
  if (custAcctId) params.set('custAcctId', custAcctId);
  if (period) params.set('period', getPeriodForReportingYear(period, year));
  if (rgnId) params.set('rgnId', rgnId);
  if (srvcLnId) params.set('srvcLnId', srvcLnId);
  if (year) params.set('year', String(year).split(',')[0]);
  if (headcount) params.set('headcount', headcount);
  if (parentAccountLimit) params.set('parentAccountLimit', String(parentAccountLimit));
  if (parentAccountOffset) params.set('parentAccountOffset', String(parentAccountOffset));
  if (accountLimit) params.set('accountLimit', String(accountLimit));
  if (accountOffset) params.set('accountOffset', String(accountOffset));
  if (unitSearch) params.set('unitSearch', unitSearch);
  if (parentAccountSearch) params.set('parentAccountSearch', parentAccountSearch);
  if (accountSearch) params.set('accountSearch', accountSearch);

  const query = params.toString();
  return query ? `${DO_DQI_API_ENDPOINTS.filters}?${query}` : DO_DQI_API_ENDPOINTS.filters;
};

const getFilterOptionsRequestFilters = (filters, type = '') => ({
  ...filters,
  parentAccountId: filters.custAcctId && (type === 'parentAccounts' || type === 'units')
    ? ''
    : type === 'parentAccounts' || type === 'units'
      ? ''
      : filters.parentAccountId,
  custAcctId: type === 'accounts' ? '' : filters.custAcctId,
  unitId: filters.custAcctId && type === 'units' ? '' : type === 'units' ? '' : filters.unitId,
  srvcLnId: '',
});

const mergeOptions = (currentOptions, nextOptions) => {
  const merged = [...currentOptions];
  const seen = new Set(currentOptions.map((option) => normalizeOptionKey(option.label || option.value)));

  nextOptions
    .filter((option) => option.value !== '')
    .forEach((option) => {
      const key = normalizeOptionKey(option.label || option.value);
      if (!seen.has(key)) {
        seen.add(key);
        merged.push(option);
      }
    });

  return merged;
};

const getSelectedValues = (value) => String(value || '').split(',').filter(Boolean);
const getSelectedLabels = (value) => String(value || '').split('||').filter(Boolean);

const CardLoader = ({ label = 'Loading...' }) => (
  <div className="card-loader slim-loader">
    <div className="loader-spinner" />
    <span>{label}</span>
  </div>
);

const FilterSelect = ({
  label,
  value,
  onChange,
  options,
  hasMore = false,
  isLoadingMore = false,
  onLoadMore,
  onSearchChange,
  isSearching = false,
  fallbackLabels = [],
  multiple = true,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const dropdownRef = useRef(null);
  const lastScrollTopRef = useRef(0);
  const selectedValues = multiple ? getSelectedValues(value) : getSelectedValues(value).slice(0, 1);
  const selectableOptions = options.filter((option) => option.value !== '');
  const trimmedSearchText = searchText.trim().toLowerCase();
  const filteredOptions = onSearchChange
    ? selectableOptions
    : selectableOptions.filter((option) => option.label.toLowerCase().includes(trimmedSearchText));
  const selectAllOptions = trimmedSearchText ? filteredOptions : selectableOptions;
  const selectedLabels = selectedValues
    .map((selectedValue, index) => options.find((option) => option.value === selectedValue)?.label || fallbackLabels[index] || 'Selected')
    .filter(Boolean);
  const displayLabel = selectedLabels.length === 0
    ? 'All'
    : selectedLabels.length === 1
      ? selectedLabels[0]
      : 'Multiple selected';

  useEffect(() => {
    if (!isOpen) return undefined;

    const closeOnOutsideClick = (event) => {
      if (!dropdownRef.current?.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', closeOnOutsideClick);
    return () => document.removeEventListener('mousedown', closeOnOutsideClick);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !onSearchChange) return undefined;

    const timeoutId = window.setTimeout(() => {
      onSearchChange(searchText.trim());
    }, 300);

    return () => window.clearTimeout(timeoutId);
  }, [isOpen, searchText]);

  useEffect(() => {
    if (!isOpen) setSearchText('');
  }, [isOpen]);

  const handleMenuScroll = (event) => {
    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
    const isNearBottom = scrollHeight - scrollTop - clientHeight < 24;
    const isScrollingDown = scrollTop > lastScrollTopRef.current;
    lastScrollTopRef.current = scrollTop;

    if (isNearBottom && isScrollingDown && hasMore && !isLoadingMore) {
      onLoadMore?.();
    }
  };

  const updateSelectedValues = (nextValues) => {
    const nextSelectedOptions = nextValues.map((nextValue) => {
      const option = options.find((candidate) => candidate.value === nextValue);
      return option || { value: nextValue, label: fallbackLabels[selectedValues.indexOf(nextValue)] || '' };
    });
    onChange(multiple ? nextValues.join(',') : (nextValues[0] || ''), nextSelectedOptions);
  };

  const toggleOption = (optionValue) => {
    if (!optionValue) {
      updateSelectedValues([]);
      return;
    }

    if (!multiple) {
      updateSelectedValues([optionValue]);
      setIsOpen(false);
      return;
    }

    updateSelectedValues(
      selectedValues.includes(optionValue)
        ? selectedValues.filter((selectedValue) => selectedValue !== optionValue)
        : [...selectedValues, optionValue]
    );
  };

  const toggleSelectAll = () => {
    if (label === 'Account') {
      updateSelectedValues([]);
      return;
    }

    const selectAllValues = selectAllOptions.map((option) => option.value);
    const allVisibleSelected = selectAllValues.length > 0 && selectAllValues.every((optionValue) => selectedValues.includes(optionValue));

    updateSelectedValues(
      allVisibleSelected
        ? []
        : Array.from(new Set([...selectedValues, ...selectAllValues]))
    );
  };

  return (
    <label className="do-filter-field">
      <span>{label}</span>
      <div className="filter-dropdown" ref={dropdownRef}>
        <button
          type="button"
          className="filter-dropdown-button"
          onClick={() => setIsOpen((current) => !current)}
          title={selectedLabels.join(', ') || 'All'}
        >
          <span>{displayLabel}</span>
          <span className="filter-dropdown-caret" aria-hidden="true" />
        </button>
        {isOpen && (
          <div className="filter-dropdown-menu" aria-label={label} onScroll={handleMenuScroll}>
            <div className="filter-dropdown-search">
              <span aria-hidden="true" />
              <input value={searchText} onChange={(event) => setSearchText(event.target.value)} placeholder="Search" />
            </div>
            {multiple && (
              <button
                type="button"
                className="filter-dropdown-option"
                onClick={toggleSelectAll}
                title="Select all"
              >
                <input type="checkbox" checked={selectAllOptions.length > 0 && selectAllOptions.every((option) => selectedValues.includes(option.value))} readOnly />
                <span>Select all</span>
              </button>
            )}
            {isSearching && (
              <div className="filter-dropdown-empty">Searching...</div>
            )}
            {!isSearching && trimmedSearchText && !filteredOptions.length && (
              <div className="filter-dropdown-empty">No results found</div>
            )}
            {filteredOptions.map((option) => (
              <button
                type="button"
                key={`${option.value}-${option.label}`}
                className={`filter-dropdown-option ${option.value && selectedValues.includes(option.value) ? 'selected' : ''}`}
                onClick={() => {
                  toggleOption(option.value);
                }}
                title={option.label}
              >
                <input type={multiple ? 'checkbox' : 'radio'} checked={option.value === '' ? selectedValues.length === 0 : selectedValues.includes(option.value)} readOnly />
                <span>{option.label}</span>
              </button>
            ))}
            {hasMore && !trimmedSearchText && (
              <div className="filter-dropdown-more">
                {isLoadingMore ? 'Loading...' : 'Scroll for more'}
              </div>
            )}
          </div>
        )}
      </div>
    </label>
  );
};

const FilterInput = ({ label, value, onChange, placeholder = 'All' }) => (
  <label className="do-filter-field">
    <span>{label}</span>
    <input value={value} onChange={(event) => onChange(event.target.value)} placeholder={placeholder} />
  </label>
);

const getSummaryScoreLabel = (summary) => summary?.score_label || summary?.scoreLabel || '--';
const getSummaryVariationLabel = (summary) => {
  const label = summary?.variation_label || summary?.variationLabel || null;
  return label === '--' ? null : label;
};
const getVariationArrow = (direction) => String.fromCharCode(direction === 'down' ? 8595 : 8593);

const normalizeSummaryVariation = (variation) => {
  const rawVariation = variation?.variation ?? variation?.variation_score ?? null;
  const numericVariation = rawVariation === null || rawVariation === undefined ? null : Number(rawVariation);
  const fallbackLabel = Number.isFinite(numericVariation) ? `${numericVariation.toFixed(1)}%` : null;
  const variationLabel = getSummaryVariationLabel(variation) || fallbackLabel;
  const direction = variation?.direction || (
    Number.isFinite(numericVariation)
      ? numericVariation > 0 ? 'up' : numericVariation < 0 ? 'down' : 'flat'
      : null
  );

  return {
    ...variation,
    variationLabel,
    variation_label: variationLabel,
    direction,
  };
};


const DoDqiScoreCards = ({ summary, dtaiSummary, isDoVariationLoading = false }) => {
  const directionClass = summary?.direction === 'down' ? 'down' : 'up';
  const dtaiDirectionClass = dtaiSummary?.direction === 'down' ? 'down' : 'up';
  const doVariationLabel = getSummaryVariationLabel(summary);
  const dtaiVariationLabel = getSummaryVariationLabel(dtaiSummary);

  return (
  <div className="score-card-row">
    <button type="button" className="score-card primary-score score-card-button" onClick={() => { window.location.href = '/'; }}>
      <h3>DO DQI</h3>
      <div className="score-line">
        <strong>{getSummaryScoreLabel(summary)}</strong>
        {doVariationLabel ? (
          <span className={`variation ${directionClass}`}>
            {getVariationArrow(summary.direction)} {doVariationLabel}
          </span>
        ) : isDoVariationLoading && (
          <span className="variation up">...</span>
        )}
      </div>
    </button>
    <button type="button" className="score-card secondary-score score-card-button" onClick={() => { window.location.href = '/dtai'; }}>
      <h3>DTAI DQI</h3>
      <div className="score-line">
        <strong>{getSummaryScoreLabel(dtaiSummary)}</strong>
        {dtaiVariationLabel && (
          <span className={`variation ${dtaiDirectionClass}`}>
            {getVariationArrow(dtaiSummary.direction)} {dtaiVariationLabel}
          </span>
        )}
      </div>
    </button>
  </div>
  );
};

const AccountsTable = ({ rows, meta, isLoading, pageOffset, onPageOffsetChange }) => {
  const total = Number(meta?.total_records) || rows.length;
  const currentPage = Math.floor(pageOffset / ACCOUNTS_PAGE_SIZE);
  const pageCount = Math.max(1, Math.ceil(total / ACCOUNTS_PAGE_SIZE));
  const nextOffset = meta?.next_offset;
  const lastOffset = Math.max(0, Math.floor(Math.max(0, total - 1) / ACCOUNTS_PAGE_SIZE) * ACCOUNTS_PAGE_SIZE);

  return (
  <div className="do-panel">
    <div className="do-panel-toolbar">
      <h2>DO DQI</h2>
      <div className="flag-legend">
        <span className="flag-legend-item flag-watchlist"><span className="flag-icon" />Watchlist</span>
        <span className="flag-legend-item flag-open"><span className="flag-icon" />Escalation Open</span>
        <span className="flag-legend-item flag-closed"><span className="flag-icon" />Escalation Closed</span>
      </div>
    </div>
    <div className="do-table-scroll account-table-shell">
      <table className="do-table">
        <thead>
          <tr>
            <th>Account Name</th>
            <th>DQI</th>
            <th>Performance</th>
            <th>Capability</th>
            <th>Client Feedback</th>
            <th>Responsiveness</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {isLoading ? (
            <tr>
              <td colSpan="7" className="empty-cell">Loading accounts...</td>
            </tr>
          ) : rows.length ? (
            rows.map((row) => (
              <tr key={`${row.period}-${row.customer_id}`}>
                <td>{row.customer_name}</td>
                <td className={Number(row.dqi_score) < 5 ? 'low-score-cell' : ''}>{row.dqi_label || formatNumber(row.dqi_score)}</td>
                <td>{row.performance_label || formatNumber(row.performance)}</td>
                <td>{row.capability_label || formatNumber(row.capability)}</td>
                <td>{row.client_feedback_label || formatNumber(row.client_feedback)}</td>
                <td>{row.responsiveness_label || formatNumber(row.responsiveness)}</td>
                <td>
                  <span className="action-icons">
                    {Number(row.watchlist) > 0 ? <span className="flag-icon flag-watchlist" title="Watchlist" /> : null}
                    {Number(row.escalation_open) > 0 ? <span className="flag-icon flag-open" title="Escalation Open" /> : null}
                    {Number(row.escalation_closed) > 0 ? <span className="flag-icon flag-closed" title="Escalation Closed" /> : null}
                  </span>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="7" className="empty-cell">No accounts returned for the selected period.</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
    <div className="account-pager">
      <span>
        {rows.length ? `${(pageOffset + 1).toLocaleString()}-${(pageOffset + rows.length).toLocaleString()} of ${total.toLocaleString()}` : '0 accounts'}
      </span>
      <button type="button" onClick={() => onPageOffsetChange(0)} disabled={pageOffset === 0 || isLoading}>First</button>
      <button type="button" onClick={() => onPageOffsetChange(Math.max(0, pageOffset - ACCOUNTS_PAGE_SIZE))} disabled={pageOffset === 0 || isLoading}>Previous</button>
      <span>{currentPage + 1} / {pageCount}</span>
      <button type="button" onClick={() => onPageOffsetChange(Number(nextOffset))} disabled={nextOffset == null || Number(nextOffset) <= pageOffset || isLoading}>Next</button>
      <button type="button" onClick={() => onPageOffsetChange(lastOffset)} disabled={pageOffset >= lastOffset || isLoading}>Last</button>
    </div>
  </div>
  );
};

const DetailScoreTable = ({ detail, isLoading }) => {
  const doDqiDetailRowsFromApi = detail?.data || detail?.rows || [];
  const overallScoreRowFromApi = detail?.overall || null;

  return (
  <div className="do-panel selected-account-panel">
    <div className="selected-account-header">
      <div>
        <h3>Your selected accounts</h3>
        <h2>{detail?.title || 'DQI Detail'}</h2>
      </div>
      <button type="button">View More Details</button>
    </div>

    {isLoading ? (
      <CardLoader label={`Loading ${detail?.title || 'DQI detail'}...`} />
    ) : detail?.error ? (
      <div className="do-status do-status-error">
        {detail.error}
      </div>
    ) : (
      <div className="do-table-scroll">
        <table className="do-table performance-table">
          <thead>
            <tr>
              {DO_DQI_DETAIL_TABLE_COLUMNS.map((column) => (
                <th key={column.apiField}>{column.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {doDqiDetailRowsFromApi.length ? doDqiDetailRowsFromApi.map((apiDetailRow) => (
              <tr key={apiDetailRow.subParameter}>
                {DO_DQI_DETAIL_TABLE_COLUMNS.map((column) => (
                  <td key={column.apiField}>
                    {formatDetailCell(apiDetailRow, column)}
                  </td>
                ))}
              </tr>
            )) : (
              <tr>
                <td colSpan="7" className="empty-cell">No detail rows returned.</td>
              </tr>
            )}
            <tr className="overall-row">
              <td>{overallScoreRowFromApi?.subParameter || 'Overall Weighted Score'}</td>
              <td colSpan="5" />
              <td>{formatPercent(overallScoreRowFromApi?.weightedScore)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    )}
  </div>
  );
};

const SqlQueryConsole = () => {
  const [sql, setSql] = useState('SELECT * FROM parquet_tables LIMIT 10');
  const [rows, setRows] = useState([]);
  const [columns, setColumns] = useState([]);
  const [message, setMessage] = useState('Run a read-only SQL query against backend DuckDB.');
  const [error, setError] = useState(null);
  const [executedQuery, setExecutedQuery] = useState('');
  const [isRunning, setIsRunning] = useState(false);

  const runQuery = async () => {
    const trimmedSql = sql.trim();

    if (!trimmedSql) {
      setError('Enter a SQL query.');
      return;
    }

    try {
      setIsRunning(true);
      setError(null);
      setMessage('Running backend query...');
      const response = await fetch(DO_DQI_API_ENDPOINTS.sqlQuery, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          query: trimmedSql,
          limit: 500,
        }),
      });
      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || result.error || 'Query failed.');
      }

      const nextRows = Array.isArray(result.data) ? result.data : [];
      setRows(nextRows);
      setColumns(result.columns?.length ? result.columns : nextRows.length ? Object.keys(nextRows[0]) : []);
      setExecutedQuery(result.executedQuery || result.rawQuery || trimmedSql);
      setMessage(`${Number(result.rowCount || nextRows.length).toLocaleString()} row${Number(result.rowCount || nextRows.length) === 1 ? '' : 's'} returned in ${result.executionTimeMs ?? '--'} ms.`);
    } catch (queryError) {
      setRows([]);
      setColumns([]);
      setExecutedQuery('');
      setError(queryError.message || 'Query failed.');
      setMessage('Query failed.');
    } finally {
      setIsRunning(false);
    }
  };

  return (
    <div className="query-console">
      <div className="query-console-header">
        <div>
          <h2>SQL Query Console</h2>
          <p>{DO_DQI_API_ENDPOINTS.sqlQuery}</p>
        </div>
        <div className="query-actions">
          <button type="button" onClick={() => { window.location.href = '/'; }}>Back</button>
          <button type="button" onClick={runQuery} disabled={isRunning}>
            {isRunning ? 'Running...' : 'Run Query'}
          </button>
        </div>
      </div>
      <textarea value={sql} onChange={(event) => setSql(event.target.value)} spellCheck="false" />
      <div className={`query-message ${error ? 'query-message-error' : ''}`}>{error || message}</div>
      {executedQuery && (
        <div className="executed-query">
          <div className="executed-query-header">
            <strong>Executed Query</strong>
            <button type="button" onClick={() => navigator.clipboard?.writeText(executedQuery)}>Copy</button>
          </div>
          <pre>{executedQuery}</pre>
        </div>
      )}
      <div className="query-results">
        {columns.length ? (
          <table>
            <thead>
              <tr>{columns.map((column) => <th key={column}>{column}</th>)}</tr>
            </thead>
            <tbody>
              {rows.map((row, index) => (
                <tr key={index}>{columns.map((column) => <td key={column}>{row[column] == null ? 'NULL' : String(row[column])}</td>)}</tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="query-empty">No results to display.</div>
        )}
      </div>
    </div>
  );
};

const DoDqiDashboard = () => {
  const [filters, setFilters] = useState(() => readSharedFilters(defaultFilters));
  const [appliedFilters, setAppliedFilters] = useState(() => readSharedFilters(defaultFilters));
  const [summary, setSummary] = useState(null);
  const [dtaiSummary, setDtaiSummary] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [accountsMeta, setAccountsMeta] = useState(null);
  const [accountsOffset, setAccountsOffset] = useState(0);
  const [accountsReadyUrl, setAccountsReadyUrl] = useState(null);
  const [details, setDetails] = useState([]);
  const [status, setStatus] = useState('Loading DO DQI data...');
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAccountsLoading, setIsAccountsLoading] = useState(true);
  const [unitOptions, setUnitOptions] = useState([{ label: 'All', value: '' }]);
  const [parentAccountOptions, setParentAccountOptions] = useState([{ label: 'All', value: '' }]);
  const [accountOptions, setAccountOptions] = useState([{ label: 'All', value: '' }]);
  const [serviceLineFilterOptions, setServiceLineFilterOptions] = useState(serviceLineOptions);
  const [filterPagination, setFilterPagination] = useState({
    parentAccounts: { nextOffset: null },
    accounts: { nextOffset: null },
  });
  const [loadingFilterPage, setLoadingFilterPage] = useState(null);
  const [searchingFilter, setSearchingFilter] = useState(null);
  const [filterSearchText, setFilterSearchText] = useState({
    units: '',
    parentAccounts: '',
    accounts: '',
  });
  const latestFiltersRef = useRef(filters);
  const skipInitialDependentFiltersRef = useRef(true);
  const filterPageLocksRef = useRef({
    parentAccounts: { isLoading: false, offset: null },
    accounts: { isLoading: false, offset: null },
  });
  const defaultFilterOptionsRef = useRef({
    units: [{ label: 'All', value: '' }],
    parentAccounts: [{ label: 'All', value: '' }],
    accounts: [{ label: 'All', value: '' }],
    serviceLines: serviceLineOptions,
  });
  const defaultFilterPaginationRef = useRef({
    parentAccounts: { nextOffset: null },
    accounts: { nextOffset: null },
  });
  const endpointUrl = useMemo(() => buildPerformanceUrl(appliedFilters), [appliedFilters]);
  const summaryUrl = useMemo(() => buildSummaryUrl(appliedFilters), [appliedFilters]);
  const summaryVariationUrl = useMemo(() => buildSummaryVariationUrl(appliedFilters), [appliedFilters]);
  const dtaiSummaryUrl = useMemo(() => buildDtaiSummaryUrl(appliedFilters), [appliedFilters]);
  const capabilityUrl = useMemo(() => buildDoDqiDetailUrl(DO_DQI_API_ENDPOINTS.capability, appliedFilters), [appliedFilters]);
  const clientFeedbackUrl = useMemo(() => buildDoDqiDetailUrl(DO_DQI_API_ENDPOINTS.clientFeedback, appliedFilters), [appliedFilters]);
  const responsivenessUrl = useMemo(() => buildDoDqiDetailUrl(DO_DQI_API_ENDPOINTS.responsiveness, appliedFilters), [appliedFilters]);
  const accountsUrl = useMemo(() => buildAccountsUrl(appliedFilters, accountsOffset), [appliedFilters, accountsOffset]);
  const revenueNoData = useMemo(() => shouldShowNoDataForRevenue(appliedFilters), [appliedFilters]);

  useEffect(() => {
    latestFiltersRef.current = filters;
    writeSharedFilters(filters);
  }, [filters]);
  useEffect(() => {
    let ignore = false;

    const loadFilterOptions = async () => {
      try {
        const response = await fetch(buildDoDqiFiltersUrl({
          ...getFilterOptionsRequestFilters(latestFiltersRef.current),
          parentAccountLimit: FILTER_FIRST_PAGE_SIZE,
          accountLimit: FILTER_FIRST_PAGE_SIZE,
        }));
        if (!response.ok) throw new Error(`DO DQI filters API returned ${response.status}`);

        const payload = await response.json();
        if (!ignore) {
          const nextUnitOptions = normalizeUnitOptions(payload);
          const nextServiceLineOptions = normalizeServiceLineOptions(payload);
          const nextParentAccountOptions = normalizeParentAccountOptions(payload, '');
          const nextAccountOptions = normalizeAccountOptions(payload, '');
          const nextPagination = payload.pagination || {
            parentAccounts: { nextOffset: null },
            accounts: { nextOffset: null },
          };

          defaultFilterOptionsRef.current = {
            units: nextUnitOptions,
            parentAccounts: nextParentAccountOptions,
            accounts: nextAccountOptions,
            serviceLines: nextServiceLineOptions,
          };
          defaultFilterPaginationRef.current = nextPagination;
          setUnitOptions(nextUnitOptions);
          setServiceLineFilterOptions(nextServiceLineOptions);
          setParentAccountOptions(nextParentAccountOptions);
          setAccountOptions(nextAccountOptions);
          setFilterPagination(nextPagination);
        }
      } catch (loadError) {
        if (!ignore) setError(loadError.message || 'Failed to load filter data');
      }
    };

    loadFilterOptions();

    return () => {
      ignore = true;
    };
  }, []);

  useEffect(() => {
    let ignore = false;

    const loadDependentFilterOptions = async () => {
      if (skipInitialDependentFiltersRef.current) {
        skipInitialDependentFiltersRef.current = false;
        return;
      }

      try {
        const response = await fetch(buildDoDqiFiltersUrl({
          ...getFilterOptionsRequestFilters(filters),
          parentAccountLimit: FILTER_FIRST_PAGE_SIZE,
          accountLimit: FILTER_FIRST_PAGE_SIZE,
        }));
        if (!response.ok) throw new Error(`DO DQI filters API returned ${response.status}`);

        const payload = await response.json();
        if (!ignore) {
          const nextUnitOptions = normalizeUnitOptions(payload);
          const nextParentAccountOptions = normalizeParentAccountOptions(payload, filters.unitId);
          const nextServiceLineOptions = mergeOptions(
            defaultFilterOptionsRef.current.serviceLines,
            normalizeServiceLineOptions(payload),
          );
          const nextAccountOptions = normalizeAccountOptions(payload, filters.unitId);
          const nextPagination = payload.pagination || {
            parentAccounts: { nextOffset: null },
            accounts: { nextOffset: null },
          };

          defaultFilterOptionsRef.current = {
            ...defaultFilterOptionsRef.current,
            units: filters.custAcctId ? nextUnitOptions : defaultFilterOptionsRef.current.units,
            parentAccounts: nextParentAccountOptions,
            accounts: nextAccountOptions,
            serviceLines: nextServiceLineOptions,
          };
          defaultFilterPaginationRef.current = nextPagination;
          if (filters.custAcctId) setUnitOptions(nextUnitOptions);
          setServiceLineFilterOptions(nextServiceLineOptions);
          setParentAccountOptions(nextParentAccountOptions);
          setAccountOptions(nextAccountOptions);
          setFilterPagination(nextPagination);
        }
      } catch (loadError) {
        if (!ignore) {
          setParentAccountOptions([{ label: 'All', value: '' }]);
          setAccountOptions([{ label: 'All', value: '' }]);
          setError(loadError.message || 'Failed to load parent account data');
        }
      }
    };

    loadDependentFilterOptions();

    return () => {
      ignore = true;
    };
  }, [
    filters.headcount,
    filters.parentAccountId,
    filters.period,
    filters.rgnId,
    filters.srvcLnId,
    filters.unitId,
    filters.year,
    filters.custAcctId,
  ]);

  useEffect(() => {
    let ignore = false;

    const loadPerformance = async () => {
      try {
        setIsLoading(true);
        setError(null);
        setStatus('Fetching summary and performance data...');
        setSummary(null);
        setDtaiSummary(null);
        setAccounts([]);
        setAccountsMeta(null);
        setAccountsReadyUrl(null);
        setDetails(DO_DQI_DETAIL_PLACEHOLDERS);

        if (revenueNoData) {
          if (ignore) return;
          setSummary(emptySummary);
          setDtaiSummary(emptySummary);
        } else {
          const [loadedSummary, loadedDtaiSummary] = await Promise.all([
            loadDoDqiSummaryIntoDuckDb(summaryUrl),
            fetch(dtaiSummaryUrl).then((response) => {
              if (!response.ok) throw new Error(`DTAI DQI summary API returned ${response.status}`);
              return response.json();
            })
          ]);

          if (ignore) return;

          setSummary(loadedSummary);
          setDtaiSummary(loadedDtaiSummary);
        }

        if (revenueNoData) {
          setDetails(DO_DQI_DETAIL_PLACEHOLDERS.map((detail) => ({
            ...blankDetailScores(detail),
            isLoading: false,
          })));
          setIsLoading(false);
          setStatus('DO DQI data loaded');
          return;
        }

        setIsLoading(false);
        setStatus('Fetching detail data...');

        const detailLoaders = [
          { key: 'performance', load: () => loadDoDqiDetail(endpointUrl) },
          { key: 'clientFeedback', load: () => loadDoDqiDetail(clientFeedbackUrl) },
          { key: 'responsiveness', load: () => loadDoDqiDetail(responsivenessUrl) },
          { key: 'capability', load: () => loadDoDqiDetail(capabilityUrl) },
        ];

        const loadDetail = async ({ key, load }) => {
          try {
            const loadedDetail = await load();
            if (ignore) return;
            setDetails((currentDetails) => currentDetails.map((detail) => (
              detail.key === key
                ? { ...loadedDetail, key, isLoading: false }
                : detail
            )));
          } catch (detailError) {
            if (ignore) return;
            setDetails((currentDetails) => currentDetails.map((detail) => (
              detail.key === key
                ? { ...detail, isLoading: false, error: detailError.message || 'Failed to load detail data' }
                : detail
            )));
            setError(detailError.message || 'Failed to load detail data');
          }
        };

        await Promise.allSettled(detailLoaders.slice(0, 3).map(loadDetail));
        if (ignore) return;
        setAccountsReadyUrl(accountsUrl);
        await loadDetail(detailLoaders[3]);

        if (ignore) return;

        try {
          const variationResponse = await fetch(summaryVariationUrl);
          if (!variationResponse.ok) throw new Error(`DO DQI summary variation API returned ${variationResponse.status}`);
          const variation = normalizeSummaryVariation(await variationResponse.json());
          if (!ignore) {
            setSummary((currentSummary) => currentSummary ? { ...currentSummary, ...variation } : currentSummary);
          }
        } catch (variationError) {
          if (!ignore) setError(variationError.message || 'Failed to load DO DQI summary variation');
        }

        setStatus('DO DQI data loaded');
      } catch (loadError) {
        if (ignore) return;
        setError(loadError.message || 'Failed to load performance data');
        setStatus('Performance data error');
      } finally {
        if (!ignore) setIsLoading(false);
      }
    };

    loadPerformance();

    return () => {
      ignore = true;
    };
  }, [accountsUrl, capabilityUrl, clientFeedbackUrl, dtaiSummaryUrl, endpointUrl, responsivenessUrl, revenueNoData, summaryUrl, summaryVariationUrl]);

  useEffect(() => {
    let ignore = false;

    const loadAccounts = async () => {
      if (!revenueNoData && accountsReadyUrl !== accountsUrl) {
        setIsAccountsLoading(true);
        return;
      }

      try {
        setIsAccountsLoading(true);
        setError(null);

        if (revenueNoData) {
          if (!ignore) {
            setAccounts([]);
            setAccountsMeta({ total_records: 0, next_offset: null });
          }
          return;
        }

        const loaded = await loadDoDqiAccountsIntoDuckDb(accountsUrl);
        const queried = await queryDoDqiAccounts();

        if (ignore) return;

        setAccounts(queried);
        setAccountsMeta(loaded);
      } catch (loadError) {
        if (ignore) return;
        setError(loadError.message || 'Failed to load account data');
      } finally {
        if (!ignore) setIsAccountsLoading(false);
      }
    };

    loadAccounts();

    return () => {
      ignore = true;
    };
  }, [accountsReadyUrl, accountsUrl, revenueNoData]);

  useEffect(() => {
    const selectedAccountIds = new Set(getSelectedValues(filters.custAcctId));
    if (!selectedAccountIds.size || !accounts.length) return;

    const selectedAccountOptions = accounts
      .filter((account) => selectedAccountIds.has(String(account.customer_id)))
      .map((account) => ({
        label: String(account.customer_name || account.customer_id),
        value: String(account.customer_id),
      }));

    if (selectedAccountOptions.length) {
      setAccountOptions((current) => mergeOptions(current, selectedAccountOptions));
    }
  }, [accounts, filters.custAcctId]);

  useEffect(() => {
    const selectedValues = getSelectedValues(filters.custAcctId);
    if (!selectedValues.length) return;

    const selectedLabels = selectedValues
      .map((selectedValue) => accountOptions.find((option) => option.value === selectedValue)?.label)
      .filter(Boolean);

    if (selectedLabels.length === selectedValues.length && selectedLabels.join('||') !== filters.custAcctLabels) {
      setFilters((current) => ({ ...current, custAcctLabels: selectedLabels.join('||') }));
    }
  }, [accountOptions, filters.custAcctId, filters.custAcctLabels]);

  const updateFilter = (key, value) => {
    setFilters((current) => {
      const next = { ...current, [key]: value };
      if (key === 'year') {
        next.custAcctId = '';
        next.custAcctLabels = '';
      }
      return next;
    });
  };

  const updateAccountFilter = (value, selectedOptions = []) => {
    setFilters((current) => ({
      ...current,
      custAcctId: value,
      custAcctLabels: selectedOptions.map((option) => option.label).filter(Boolean).join('||'),
    }));
  };

  const searchFilterOptions = async (type, searchText) => {
    const requestFilters = { ...latestFiltersRef.current };
    const optionRequestFilters = getFilterOptionsRequestFilters(requestFilters, type);
    const trimmedSearchText = searchText.trim();

    try {
      setFilterSearchText((current) => ({ ...current, [type]: trimmedSearchText }));
      if (!trimmedSearchText) {
        if (type === 'units') {
          setUnitOptions(defaultFilterOptionsRef.current.units);
        } else if (type === 'parentAccounts') {
          setParentAccountOptions(defaultFilterOptionsRef.current.parentAccounts);
          setFilterPagination((current) => ({
            ...current,
            parentAccounts: defaultFilterPaginationRef.current.parentAccounts || { nextOffset: null },
          }));
        } else {
          setAccountOptions(defaultFilterOptionsRef.current.accounts);
          setFilterPagination((current) => ({
            ...current,
            accounts: defaultFilterPaginationRef.current.accounts || { nextOffset: null },
          }));
        }
      }
      setSearchingFilter(type);
      const response = await fetch(buildDoDqiFiltersUrl({
        ...optionRequestFilters,
        unitSearch: type === 'units' ? trimmedSearchText : undefined,
        parentAccountSearch: type === 'parentAccounts' ? trimmedSearchText : undefined,
        accountSearch: type === 'accounts' ? trimmedSearchText : undefined,
        parentAccountLimit: type === 'parentAccounts' ? FILTER_FIRST_PAGE_SIZE : 1,
        parentAccountOffset: 0,
        accountLimit: type === 'accounts' ? FILTER_FIRST_PAGE_SIZE : 1,
        accountOffset: 0,
      }));
      if (!response.ok) throw new Error(`DO DQI filters API returned ${response.status}`);

      const payload = await response.json();
      const currentFilters = latestFiltersRef.current;

      if (
        currentFilters.unitId !== requestFilters.unitId
        || currentFilters.parentAccountId !== requestFilters.parentAccountId
        || currentFilters.period !== requestFilters.period
        || currentFilters.year !== requestFilters.year
        || currentFilters.headcount !== requestFilters.headcount
        || currentFilters.rgnId !== requestFilters.rgnId
        || currentFilters.srvcLnId !== requestFilters.srvcLnId
      ) {
        return;
      }

      if (type === 'units') {
        const nextUnitOptions = normalizeUnitOptions(payload);
        if (!trimmedSearchText) {
          defaultFilterOptionsRef.current = {
            ...defaultFilterOptionsRef.current,
            units: nextUnitOptions,
          };
        }
        setUnitOptions(nextUnitOptions);
      } else if (type === 'parentAccounts') {
        const nextParentAccountOptions = normalizeParentAccountOptions(payload, optionRequestFilters.unitId);
        if (!trimmedSearchText) {
          defaultFilterOptionsRef.current = {
            ...defaultFilterOptionsRef.current,
            parentAccounts: nextParentAccountOptions,
          };
        }
        setParentAccountOptions(nextParentAccountOptions);
      } else {
        const nextAccountOptions = normalizeAccountOptions(payload, optionRequestFilters.unitId);
        if (!trimmedSearchText) {
          defaultFilterOptionsRef.current = {
            ...defaultFilterOptionsRef.current,
            accounts: nextAccountOptions,
          };
        }
        setAccountOptions(nextAccountOptions);
      }

      if (type !== 'units') {
        const nextPagination = payload.pagination?.[type] || { nextOffset: null };
        if (!trimmedSearchText) {
          defaultFilterPaginationRef.current = {
            ...defaultFilterPaginationRef.current,
            [type]: nextPagination,
          };
        }
        setFilterPagination((current) => ({
          ...current,
          [type]: nextPagination,
        }));
      }
    } catch (loadError) {
      setError(loadError.message || 'Failed to search filter records');
    } finally {
      setSearchingFilter(null);
    }
  };

  const loadMoreFilterOptions = async (type) => {
    const paginationKey = type === 'parentAccounts' ? 'parentAccounts' : 'accounts';
    const nextOffset = filterPagination[paginationKey]?.nextOffset;
    const totalRecords = Number(filterPagination[paginationKey]?.totalRecords || 0);
    const loadedRecords = Math.max(0, (paginationKey === 'parentAccounts' ? parentAccountOptions : accountOptions).length - 1);

    if (
      nextOffset == null
      || (totalRecords > 0 && loadedRecords >= totalRecords)
      || loadingFilterPage === paginationKey
      || filterPageLocksRef.current[paginationKey]?.isLoading
      || filterPageLocksRef.current[paginationKey]?.offset === nextOffset
    ) {
      return;
    }

    const requestFilters = { ...latestFiltersRef.current };
    const optionRequestFilters = getFilterOptionsRequestFilters(requestFilters, paginationKey);

    try {
      filterPageLocksRef.current[paginationKey] = { isLoading: true, offset: nextOffset };
      setLoadingFilterPage(paginationKey);
      const response = await fetch(buildDoDqiFiltersUrl({
        ...optionRequestFilters,
        parentAccountSearch: paginationKey === 'parentAccounts' ? filterSearchText.parentAccounts : undefined,
        accountSearch: paginationKey === 'accounts' ? filterSearchText.accounts : undefined,
        parentAccountLimit: paginationKey === 'parentAccounts' ? FILTER_NEXT_PAGE_SIZE : 1,
        parentAccountOffset: paginationKey === 'parentAccounts' ? nextOffset : 0,
        accountLimit: paginationKey === 'accounts' ? FILTER_NEXT_PAGE_SIZE : 1,
        accountOffset: paginationKey === 'accounts' ? nextOffset : 0,
      }));
      if (!response.ok) throw new Error(`DO DQI filters API returned ${response.status}`);

      const payload = await response.json();
      const currentFilters = latestFiltersRef.current;

      if (
        currentFilters.unitId !== requestFilters.unitId
        || currentFilters.parentAccountId !== requestFilters.parentAccountId
        || currentFilters.period !== requestFilters.period
        || currentFilters.year !== requestFilters.year
        || currentFilters.headcount !== requestFilters.headcount
        || currentFilters.rgnId !== requestFilters.rgnId
        || currentFilters.srvcLnId !== requestFilters.srvcLnId
      ) {
        return;
      }

      if (paginationKey === 'parentAccounts') {
        setParentAccountOptions((current) => mergeOptions(current, normalizeParentAccountOptions(payload, optionRequestFilters.unitId)));
      } else {
        setAccountOptions((current) => mergeOptions(current, normalizeAccountOptions(payload, optionRequestFilters.unitId)));
      }

      setFilterPagination((current) => ({
        ...current,
        [paginationKey]: payload.pagination?.[paginationKey] || { nextOffset: null },
      }));
    } catch (loadError) {
      setError(loadError.message || 'Failed to load more filter records');
    } finally {
      filterPageLocksRef.current[paginationKey] = { isLoading: false, offset: null };
      setLoadingFilterPage(null);
    }
  };

  const handleUnitChange = (unitId) => {
    setParentAccountOptions([{ label: 'All', value: '' }]);
    setAccountOptions([{ label: 'All', value: '' }]);
    setFilterSearchText({ units: '', parentAccounts: '', accounts: '' });
    defaultFilterOptionsRef.current = {
      ...defaultFilterOptionsRef.current,
      parentAccounts: [{ label: 'All', value: '' }],
      accounts: [{ label: 'All', value: '' }],
    };
    defaultFilterPaginationRef.current = {
      parentAccounts: { nextOffset: null },
      accounts: { nextOffset: null },
    };
    filterPageLocksRef.current = {
      parentAccounts: { isLoading: false, offset: null },
      accounts: { isLoading: false, offset: null },
    };
    setLoadingFilterPage(null);
    setFilterPagination({
      parentAccounts: { nextOffset: null },
      accounts: { nextOffset: null },
    });
    setFilters((current) => ({
      ...current,
      unitId,
      parentAccountId: '',
      custAcctId: '',
      custAcctLabels: '',
    }));
  };

  const handleParentAccountChange = (parentAccountId) => {
    setAccountOptions([{ label: 'All', value: '' }]);
    setFilterSearchText((current) => ({ ...current, accounts: '' }));
    defaultFilterOptionsRef.current = {
      ...defaultFilterOptionsRef.current,
      accounts: [{ label: 'All', value: '' }],
    };
    defaultFilterPaginationRef.current = {
      ...defaultFilterPaginationRef.current,
      accounts: { nextOffset: null },
    };
    filterPageLocksRef.current.accounts = { isLoading: false, offset: null };
    setLoadingFilterPage(null);
    setFilterPagination((current) => ({
      ...current,
      accounts: { nextOffset: null },
    }));
    setFilters((current) => ({
      ...current,
      parentAccountId,
      custAcctId: '',
      custAcctLabels: '',
    }));
  };

  const clearFilters = () => {
    setFilters(defaultFilters);
  };

  const submitFilters = () => {
    resetDoDqiData();
    setAccountsOffset(0);
    setAppliedFilters(filters);
  };

  return (
    <div className="do-dashboard">
      <header className="do-topbar">
        <div className="brand">enterprise<span>360</span></div>
        <div className="user-block">
          <span>KUMAR, VARUN</span>
          <strong>Executive</strong>
        </div>
      </header>

      <main className="do-shell">
        <section className="do-intro">
          <p>
            <strong>Delivery Quality Index (DQI)</strong> is a comprehensive measure of quality of delivery health.
            Refer here: <a href="/">DQI Playbook</a> | <a href="/">FAQ</a>
          </p>
          <div><strong>Last Updated:</strong> 5 August, 2026</div>
        </section>

        <section className="do-filter-grid">
          <FilterSelect
            label="Unit"
            value={filters.unitId}
            onChange={handleUnitChange}
            options={unitOptions}
            onSearchChange={(searchText) => searchFilterOptions('units', searchText)}
            isSearching={searchingFilter === 'units'}
          />
          <FilterSelect
            label="Parent Account"
            value={filters.parentAccountId}
            onChange={handleParentAccountChange}
            options={parentAccountOptions}
            hasMore={filterPagination.parentAccounts?.nextOffset != null}
            isLoadingMore={loadingFilterPage === 'parentAccounts'}
            onLoadMore={() => loadMoreFilterOptions('parentAccounts')}
            onSearchChange={(searchText) => searchFilterOptions('parentAccounts', searchText)}
            isSearching={searchingFilter === 'parentAccounts'}
          />
          <FilterSelect
            label="Account"
            value={filters.custAcctId}
            onChange={updateAccountFilter}
            options={accountOptions}
            fallbackLabels={getSelectedLabels(filters.custAcctLabels)}
            hasMore={filterPagination.accounts?.nextOffset != null}
            isLoadingMore={loadingFilterPage === 'accounts'}
            onLoadMore={() => loadMoreFilterOptions('accounts')}
            onSearchChange={(searchText) => searchFilterOptions('accounts', searchText)}
            isSearching={searchingFilter === 'accounts'}
          />
          <FilterSelect label="Service Line" value={filters.srvcLnId} onChange={(value) => updateFilter('srvcLnId', value)} options={serviceLineFilterOptions} />
          <FilterSelect label="Delivery Region" value={filters.rgnId} onChange={(value) => updateFilter('rgnId', value)} options={deliveryRegionOptions} />
          <FilterSelect label="Revenue" value={filters.revenue} onChange={(value) => updateFilter('revenue', value)} options={revenueOptions} />
          <FilterSelect label="Reporting Year" value={filters.year} onChange={(value) => updateFilter('year', value)} options={reportingYearOptions} multiple={false} />
          <FilterSelect label="Reporting Period" value={filters.period} onChange={(value) => updateFilter('period', value)} options={periodOptions} multiple={false} />
          <FilterSelect label="Headcount" value={filters.headcount} onChange={(value) => updateFilter('headcount', value)} options={headcountOptions} />
          <button type="button" className="submit-button" onClick={submitFilters}>Submit</button>
          <button type="button" className="clear-button" onClick={clearFilters}>Clear Filters</button>
        </section>

        <section className="do-tabs">
          <button type="button">DQI SUMMARY</button>
          <button type="button" className="active">DQI DETAIL VIEW</button>
          <div className="do-actions">
            <button type="button" onClick={() => { window.location.href = '/db'; }}>SQL Query</button>
            <button type="button" onClick={() => { window.location.href = '/dtai'; }}>DQI (with Transformation & Risk Index)</button>
            <button type="button">Email as PDF</button>
          </div>
        </section>

        {error && (
          <div className="do-status do-status-error">
            {error}
          </div>
        )}

        <div className="note-banner">*Account-Service Line-Delivery Region Combination with HC&lt;10 are not included here</div>
        <DoDqiScoreCards summary={summary} dtaiSummary={dtaiSummary} />
        <div className="note-text">*The variation in DQI score shown is from last month score*</div>

        <section className="do-detail-grid">
          <AccountsTable
            rows={accounts}
            meta={accountsMeta}
            isLoading={isAccountsLoading}
            pageOffset={accountsOffset}
            onPageOffsetChange={(offset) => {
              resetDoDqiData();
              setAccountsOffset(offset);
            }}
          />
          <div className="detail-table-stack">
            {(details.length ? details : DO_DQI_DETAIL_PLACEHOLDERS).map((detail) => (
              <DetailScoreTable key={detail.key || detail.title} detail={detail} isLoading={Boolean(detail.isLoading)} />
            ))}
          </div>
        </section>
      </main>
    </div>
  );
};

const App = () => {
  if (window.location.pathname === '/dtai') {
    return <DtaiDqiDashboard />;
  }

  if (window.location.pathname === '/db') {
    return (
      <div className="do-dashboard">
        <header className="do-topbar">
          <div className="brand">enterprise<span>360</span></div>
        </header>
        <main className="do-shell">
          <SqlQueryConsole />
        </main>
      </div>
    );
  }

  return <DoDqiDashboard />;
};

export default App;











